
import { Component, Inject, OnInit, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { LoadingService } from "@core/loading.service";
import { ITimer } from "@interfaces/timer";
import { IonicModule, ModalController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Observable, Subscription, concat, interval, of, throwError } from "rxjs";
import { catchError, delay, map, take, timeout } from "rxjs/operators";
import { JoinedSyndicateComponent } from "src/app/components/joined-syndicate/joined-syndicate.component";
import { CustomAnimation } from "src/app/directives/modal-transistion";
import { PaymentService } from "src/app/services/payment.service";
import { TrackedEventsService } from "src/app/services/tracked-events.service";
import { DisplayCurrencyPipe } from "../../../pipe/display-currency.pipe";
import { DayPipe } from "../../../pipe/day";
import { SyndicateTicketsComponent } from "../../syndicate/syndicate-tickets/syndicate-tickets.component";
import { SyndicateRepositoryService } from "../../syndicate/syndicate-repository.service";

@Component({
  selector: "app-credit-checkout",
  styleUrls: ["./credit-checkout.component.scss"],
  templateUrl: "./credit-checkout.component.html",
  standalone: true,
  imports: [IonicModule, MatProgressSpinner, DisplayCurrencyPipe, DayPipe, TranslatePipe],
})
export class CreditCheckoutComponent implements OnInit {
  public currencySymbol: string;
  public preferredCurrency: string;
  public languageCode: string;
  public languageCodeSignal = signal<string>("");
  public preferredCurrencySignal = signal<string>("");
  public creditCheckoutData: Data;
  public spinnerAnimation: boolean;
  public progressBar: number;
  public proceed: boolean;
  public creditPaymentInProgress = false;
  public timer$: Observable<number>;
  public sub: Subscription;
  public $timer: ITimer;

  constructor(
    public modalCtrl: ModalController,
    public authService: AuthService,
    public translate: TranslateService,
    public loadingService: LoadingService,
    private trackedEventsService: TrackedEventsService,
    private paymentService: PaymentService,
    private router: Router,
    private matDailog: MatDialog,
    private modalController: ModalController,
    @Inject(MAT_DIALOG_DATA) public data: Data,
     private syndicateService: SyndicateRepositoryService
  ) {
    this.currencySymbol = "£";
    this.preferredCurrency = "GBP";
    this.languageCode = "en";
    const five = 5;
    this.progressBar = five;
    this.proceed = true;
    this.loadingService.present();
    this.authService.userDetails$.subscribe({
      next: (user: Data) => {
        this.languageCodeSignal.set(user?.language_code ? user?.language_code : "en");
        this.languageCode = this.languageCodeSignal();
        translate.use(this.languageCodeSignal() ? this.languageCodeSignal() : "en");
        this.preferredCurrencySignal.set(user?.preferred_currency ? user?.preferred_currency : "GBP");
        this.preferredCurrency = this.preferredCurrencySignal();
        this.authService.getCurrencyList$.subscribe({
          next: (currencyData: Data) => {
            const CurrencyDetails = currencyData?.details?.find(
              (x: Data) => x?.currency_code === this.preferredCurrency
            );
            this.currencySymbol = CurrencyDetails?.currency_symbol ? CurrencyDetails?.currency_symbol : "£";
          },
          error: (err) => {
            return err;
          },
        });
      },
      error: (err) => {
        return err;
      },
    });
  }

  public ngOnInit(): void {
    const timeDelay = 3000;
    if (this.data?.checkout_description) {
      this.authService.currencyNodeDvData = this.data;
      this.authService.getDVFromString(this.data?.checkout_description).then((dvString: string) => {
        this.data.checkout_description = dvString;
      });
    }
    this.creditCheckoutData = this.data;
    setTimeout(() => {
      this.loadingService.dismiss();
    }, timeDelay);
  }

  public goBack(): void {
    if (this.creditPaymentInProgress) {
      return;
    }
    this.modalCtrl.dismiss(true).catch(() => {
      return false;
    });
  }

  public onJoinClick(): void {
    if (this.creditPaymentInProgress) {
      return;
    }
    this.creditPaymentInProgress = true;
    this.startSpinner();
    this.makeCreditPayment();
  }

  private releaseCreditPaymentLock(resetSpinner = true): void {
    this.creditPaymentInProgress = false;
    if (resetSpinner) {
      this.spinnerAnimation = false;
      this.proceed = true;
      this.sub?.unsubscribe();
    }
  }

  /* public makeCreditPayment(): void {
    try {
      const timeDelay = 3000;
      setTimeout(() => {
        this.modalCtrl
          .dismiss(true)
          .catch(() => {
            return false;
          });
      }, timeDelay);
    } catch (err) {
      return err;
    }
  } */

  makeCreditPayment() {
    try {
      let eventName;
      let isUpdatedCelebration;
      const selectedOffersCredit = this.data ? JSON.parse(JSON.stringify(this.data)) : {};
      this.paymentService?.checkCreditBalance()?.subscribe((res: Data) => {
        let balanceDetails: Data = res ? res?.data() : "";
        if (balanceDetails && balanceDetails?.credit && balanceDetails?.credit >= this.data?.price) {
          this.paymentService?.doCreditPayment(this.data)?.pipe(
            timeout(3000),
            catchError((err) => {
              if (err?.name === "TimeoutError") {
                this.authService.getConstantsMsg("1163").then((translatedMsg) => {
                  this.authService.showToastMessage(translatedMsg, {
                    message: {
                      line_1: "Your tickets are being generated. You'll see them shortly in your syndicate tab.",
                    },
                    style: {
                      icon: "tick circle success.png",
                      type: "Success",
                    },
                  });
                });
                this.releaseCreditPaymentLock();
                this.goBack();
                return of(null);
              }
              return throwError(() => err);
            })
          )?.subscribe({
            next: (result: Data) => {
            if (!result || !result?.response) {
              this.releaseCreditPaymentLock();
              return;
            }
            this.progressBar = 100;
            this.sub?.unsubscribe();
            const responsObj = {
              ...selectedOffersCredit,
              ...result?.response,
              productName: this.data?.lottery,
            };
            if (result?.status === "rejected") {
              this.releaseCreditPaymentLock();
              this.authService
                .getConstantsMsg(result?.error_code)
                .then((translatedMsg) => {
                  const defaultMsg = {
                    message: {
                      line_1: "Please wait a few minutes between each request.",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle error.svg",
                      type: "Error",
                    },
                  };
                  this.authService?.showToastMessage(translatedMsg, defaultMsg);
                })
                .catch((err) => {
                  return err;
                });
              return;
            }
            if (result && result?.response && result?.response?.status === "SUCCESS") {
              this.openSuccessInfoPopup(responsObj);
            } else {
              this.releaseCreditPaymentLock();
              this.authService
                .getConstantsMsg("1104")
                .then((translatedMsg) => {
                  const defaultMsg = {
                    message: {
                      line_1: "Please wait a few minutes between each request.",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle error.svg",
                      type: "Error",
                    },
                  };
                  this.authService.showToastMessage(translatedMsg, defaultMsg);
                })
                .catch((err) => {
                  return err;
                });
              return;
            }
            const timeDelay1 = 2000;
            const timeDelay2 = 300;
            setTimeout(() => {
              this.spinnerAnimation = false;
              this.proceed = false;
              setTimeout(() => {
                this.proceed = true;
                this.releaseCreditPaymentLock(false);
              }, timeDelay1);
            }, timeDelay2);
            },
            error: (err) => {
              this.releaseCreditPaymentLock();
              const translatedMsg = "";
              const defaultMsg = {
                message: {
                  line_1: "Some error has occured.",
                  line_2: err,
                },
                style: {
                  type: "Error",
                  icon: "tick circle error.svg",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            },
          });
        } else {
          this.releaseCreditPaymentLock();
          this.authService.getConstantsMsg("1055").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "You do not have enough credit to complete this transaction",
                line_2: "",
              },
              style: {
                type: "Error",
                icon: "tick circle error.svg",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          });
        }
      });

      //NEW CALL END

      eventName = "join_managed_one-off_credit_syndicate";

      this.trackedEventsService?.listOfEvents$?.subscribe({
        next: (d: { id: string }[]) => {
          if (d.some((f) => f.id === eventName)) {
            this.trackedEventsService?.updateTrackedUserEvent(eventName);
          }
        },
        error: (err) => {
          return err;
        },
      });

      this.trackedEventsService.listOfEvents$.subscribe({
        next: (d: { id: string }[]) => {
          d?.forEach((data: { id: string }) => {
            if (data?.id === eventName && !isUpdatedCelebration) {
              this.trackedEventsService.updateCelebration(data);
              isUpdatedCelebration = true;
            }
          });
        },
        error: (err) => {
          return err;
        },
      });
    } catch (err) {
      throwError(err);
    }
  }

public openSuccessInfoPopup(syndicateObj): void {
  const lottery = syndicateObj?.lottery;
  if (!lottery) {
    // handle or log missing lottery
    return;
  }
  this.syndicateService.getNextDrawDay(lottery).pipe(take(1)).subscribe({
    next: (nextDrawDay: string) => {
      const dialogData = { ...syndicateObj, current_draw_day: nextDrawDay };
      const confirmationDialog = this.matDailog.open(JoinedSyndicateComponent, {
        backdropClass: "cashout-back-drop-color",
        data: dialogData,
        panelClass: "joined-syndicate-dialog",
      });

      confirmationDialog.afterClosed().subscribe({
          next: async (dialogResponse: boolean) => {
                        if (dialogResponse) {
              try {
                if (!syndicateObj?.encrypt_syndicate_id) {
                  this.router.navigateByUrl("mainTabs/tabs/syndicate");
                } else {
                  this.paymentService
                    .getSyndicateInfoWithProducts(syndicateObj?.encrypt_syndicate_id)
                    .pipe(
                      map((detail: Data) => {
                        const zero = 0;
                        const syndicateInfo = detail[zero]?.data();
                        if (!syndicateInfo) return;
                        syndicateInfo["product"] = [];
                        const productDocs = detail && detail[1] && detail[1].docs ? detail[1].docs : "";
                        for (let index = 0; index < productDocs.length; index++) {
                          syndicateInfo?.product?.push(productDocs[index]?.data());
                        }
                        if (!syndicateInfo) return;
                        syndicateInfo["cardType"] = "Managed";
                        return syndicateInfo;
                      })
                    )
                    .subscribe({
                      next: (syndicateInfo) => {
                        if (!syndicateInfo) return;
                        this.showTicketCredit(syndicateInfo);
                      },
                      error: (err) => {
                        throw err;
                      },
                    });
                }
                this.goBack();
              } catch (e) {
                return e;
              }
            } else {
              this.goBack();
            }

            this.trackedEventsService.openAnimation();
          },
          error: (err) => {
            this.authService.errorHandlerLogs("Error closing dialog:", err);
          }

    });
    },
    error: (err) => {
      this.authService.errorHandlerLogs("Error fetching next draw day:", err);
    }
  });
}

  public async showTicketCredit(syndicateInfo): Promise<void> {
    const syndicateTickets = await this.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: { syndicate: syndicateInfo },
      cssClass: "modal-fullScreen",
      enterAnimation: CustomAnimation.enterFromRight,
      leaveAnimation: CustomAnimation.exitThroughRight,
    });
    return await syndicateTickets.present();
  }

  public startSpinner(): void {
    this.spinnerAnimation = true;
    const zero = 0;
    const one = 1;
    const twentyFive = 25;
    const fifty = 50;
    const ninety = 90;
    const oneTwenty = 120;
    this.progressBar = zero;
    // Progressbar default code
    // this.timer$ = interval(fifty);
    // this.sub = this.timer$.subscribe(
    //   (sec) => {
    //     if (this.progressBar == fifty) {
    //       this.sub?.unsubscribe();
    //       this.timer$ = interval(ninety);
    //       this.sub = this.timer$.subscribe(() => {
    //         this.progressBar = this.progressBar + one;
    //         if (this.progressBar == seventyFive) {
    //           this.sub?.unsubscribe();
    //           this.timer$ = interval(oneTwenty);
    //           this.sub = this.timer$.subscribe(() => {
    //             this.progressBar = this.progressBar + one;
    //           });
    //         }
    //       });
    //     }
    //     this.progressBar = zero + sec;
    //   },
    //   (err) => {
    //     return err;
    //   },
    // );

    // Progressbar updated code
    const timer50$ = interval(fifty).pipe(take(fifty));
    const timer90$ = interval(ninety).pipe(take(twentyFive));
    const timer120$ = interval(oneTwenty).pipe(take(twentyFive));

    concat(timer50$, timer90$, timer120$)
      .pipe(delay(0))
      .subscribe({
        next: () => {
          this.progressBar += 1;
        },
        error: (err) => {
          return err;
        },
      });
  }
}
